import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ExternalLink,
  Globe2,
  Lock,
  Plug,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Unplug,
  XCircle,
} from 'lucide-react';
import { GlowCard, GlassSurface } from '@/components/glow-card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/spinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fadeUp } from '@/components/motion';
import {
  useDisconnectGoogle,
  useGoogleIntegrationStatus,
  useGoogleProperties,
  useSaveGoogleProperties,
} from '@/features/integrations/use-google';
import { summarizeApiError } from '@/lib/api';
import { humanizeApiError } from '@/lib/error-messages';

/**
 * Google Analytics + Search Console integration card. Per-project. Renders one
 * of three states:
 *
 *  - server not configured  → tell admin to set env vars
 *  - not connected          → explanatory pitch + "Connect Google" CTA
 *  - connected              → user details + GA4 + GSC property pickers + disconnect
 */
export function GoogleIntegrationCard({ projectId }: { projectId: string }): JSX.Element {
  const status = useGoogleIntegrationStatus(projectId);

  if (status.isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <GlassSurface className="p-6">
          <header className="flex items-center gap-3">
            <div className="h-11 w-11 animate-pulse rounded-xl bg-muted" />
            <div className="space-y-1.5">
              <div className="h-4 w-56 animate-pulse rounded bg-muted" />
              <div className="h-3 w-32 animate-pulse rounded bg-muted" />
            </div>
          </header>
          <div className="mt-5 space-y-2">
            <div className="h-3 w-full animate-pulse rounded bg-muted" />
            <div className="h-3 w-4/5 animate-pulse rounded bg-muted" />
            <div className="h-3 w-3/5 animate-pulse rounded bg-muted" />
          </div>
          <div className="mt-5 flex items-center gap-3">
            <Spinner className="h-4 w-4" />
            <span className="text-xs text-muted-foreground">
              Checking your Google connection…
            </span>
          </div>
        </GlassSurface>
      </motion.div>
    );
  }
  if (status.error) {
    return <ErrorState error={status.error} onRetry={() => void status.refetch()} />;
  }
  if (!status.data) {
    return (
      <GlassSurface className="p-6">
        <p className="text-sm text-muted-foreground">No integration data.</p>
      </GlassSurface>
    );
  }
  if (!status.data.configured) {
    return <ServerNotConfigured />;
  }
  if (!status.data.connected) {
    return <NotConnected projectId={projectId} />;
  }
  return <Connected projectId={projectId} status={status.data} />;
}

// ───────────────────────────── states ─────────────────────────────

function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}): JSX.Element {
  // Route every branch through the shared humanizer so users see the same
  // plain-English copy the rest of the app uses. Special-case a few messages
  // that are more specific to this Google integration.
  const humanized = humanizeApiError(error);
  const status =
    error && typeof error === 'object' && 'status' in error
      ? Number((error as { status: unknown }).status)
      : null;
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code: unknown }).code)
      : null;

  let title = humanized.title;
  let body = humanized.description;

  if (status === 404 || code === 'NOT_FOUND') {
    title = 'Integration not available yet';
    body =
      'This project has not been wired for Google integrations. Reload the page and try again.';
  } else if (status === 401 || code === 'JWT_INVALID' || code === 'AUTH_MISSING') {
    title = 'Your session expired';
    body = 'Sign in again to manage Google connections.';
  } else if (status === 403 || code === 'AUTH_TENANT_FORBIDDEN') {
    title = "You don't have access to this project";
    body = 'Ask a workspace admin to add you as a member of this project.';
  } else if (!status && !(error instanceof Error && (error as { code?: string }).code)) {
    title = "Can't reach the service";
    body = 'Check your network connection and try again in a moment.';
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 200, damping: 22 }}
    >
      <GlassSurface className="overflow-hidden p-6">
        <header className="flex items-center gap-3">
          <motion.div
            initial={{ rotate: -10, scale: 0.9 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{ delay: 0.1, type: 'spring', stiffness: 180 }}
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-500"
          >
            <AlertTriangle className="h-5 w-5" />
          </motion.div>
          <div>
            <h2 className="text-base font-semibold">Google Analytics + Search Console</h2>
            <p className="text-xs text-muted-foreground">{title}</p>
          </div>
        </header>

        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mt-4 text-sm text-muted-foreground"
        >
          {body}
        </motion.p>

        {/* Raw error details are intentionally not displayed. Developers can
            still inspect the underlying error via the browser console — see
            `humanizeApiError` above. */}

        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mt-5 flex items-center gap-3"
        >
          <Button onClick={onRetry} size="sm">
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
        </motion.div>
      </GlassSurface>
    </motion.div>
  );
}

function ServerNotConfigured(): JSX.Element {
  return (
    <GlassSurface className="p-6">
      <header className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15 text-amber-500">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-base font-semibold">Google Analytics + Search Console</h2>
          <p className="text-xs text-muted-foreground">Server not configured</p>
        </div>
      </header>
      <p className="mt-4 text-sm text-muted-foreground">
        The platform administrator has not configured Google OAuth credentials.
        Ask them to set <code className="rounded bg-muted px-1.5 py-0.5 text-xs">GOOGLE_OAUTH_CLIENT_ID</code>,{' '}
        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">GOOGLE_OAUTH_CLIENT_SECRET</code>, and{' '}
        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">GOOGLE_OAUTH_REDIRECT_URI</code> in the API
        environment, then restart the API.
      </p>
    </GlassSurface>
  );
}

function NotConnected({ projectId }: { projectId: string }): JSX.Element {
  const handleConnect = async (): Promise<void> => {
    try {
      const { apiGet } = await import('@/lib/api');
      const { url } = await apiGet<{ url: string }>(
        `/v1/projects/${projectId}/integrations/google/authorize`,
      );
      window.location.assign(url);
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: "Couldn't start Google sign-in",
      });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <GlowCard glow>
      <motion.div variants={fadeUp} initial="hidden" animate="show" className="p-6">
        <header className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground">
              <BarChart3 className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold">
                Google Analytics + Search Console
              </h2>
              <p className="text-xs text-muted-foreground">Not connected</p>
            </div>
          </div>
          <span className="hidden rounded-full border border-border/60 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground sm:inline-block">
            Per project
          </span>
        </header>

        <p className="mt-4 text-sm text-muted-foreground">
          Connect the Google account that owns this website to unlock real
          traffic, clicks, impressions, and ranking data in your monthly reports.
        </p>

        <ul className="mt-4 space-y-2 text-sm">
          <Bullet
            icon={Lock}
            text={
              <>
                <span className="font-medium text-foreground">Read-only access.</span>{' '}
                We can read GA4 + Search Console — we cannot change anything in
                your Google account.
              </>
            }
          />
          <Bullet
            icon={ShieldCheck}
            text={
              <>
                <span className="font-medium text-foreground">Encrypted storage.</span>{' '}
                Your refresh token is encrypted at rest with a server-side key
                ring. The platform never sees the raw token.
              </>
            }
          />
          <Bullet
            icon={Unplug}
            text={
              <>
                <span className="font-medium text-foreground">Disconnect anytime.</span>{' '}
                One click revokes access at Google and deletes the saved token
                here.
              </>
            }
          />
        </ul>

        <div className="mt-5 grid gap-4 rounded-xl border border-border/50 bg-muted/30 p-4 sm:grid-cols-2">
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-500">
              <CheckCircle2 className="h-3.5 w-3.5" />
              With Google connected
            </div>
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
              <li>• Sessions, users, engaged sessions</li>
              <li>• Clicks, impressions, CTR, average position</li>
              <li>• Real Top Query and Top Page tables</li>
              <li>• Month-over-month deltas + anomaly flags</li>
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <XCircle className="h-3.5 w-3.5" />
              Without it
            </div>
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
              <li>• Technical SEO audit still runs</li>
              <li>• Core Web Vitals via PageSpeed still pulled</li>
              <li>• Keyword research + briefs unaffected</li>
              <li>• Reports omit the traffic + search-console sections</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button onClick={handleConnect}>
            <Plug className="h-4 w-4" /> Connect Google account
          </Button>
          <span className="text-xs text-muted-foreground">
            You will be redirected to Google to choose an account and grant
            read-only access.
          </span>
        </div>
      </motion.div>
    </GlowCard>
  );
}

function Connected({
  projectId,
  status,
}: {
  projectId: string;
  status: import('@/features/integrations/use-google').GoogleIntegrationStatus;
}): JSX.Element {
  const props = useGoogleProperties(projectId, true);
  const save = useSaveGoogleProperties(projectId);
  const disconnect = useDisconnectGoogle(projectId);
  const [ga4, setGa4] = useState<string>(status.ga4_property_id ?? '');
  const [gsc, setGsc] = useState<string>(status.gsc_site_url ?? '');

  const dirty = ga4 !== (status.ga4_property_id ?? '') || gsc !== (status.gsc_site_url ?? '');
  const needsPicking = !status.ga4_property_id || !status.gsc_site_url;

  const onSave = async (): Promise<void> => {
    try {
      await save.mutateAsync({
        ga4_property_id: ga4 || null,
        gsc_site_url: gsc || null,
      });
      toast.success('Saved — next report will use these properties');
    } catch (e) {
      const summary = summarizeApiError(e, { fallbackTitle: "Couldn't save changes" });
      toast.error(summary.title, { description: summary.description });
    }
  };

  const onDisconnect = async (): Promise<void> => {
    if (!window.confirm('Disconnect this Google account from this project?')) return;
    try {
      await disconnect.mutateAsync();
      toast.success('Disconnected');
    } catch (e) {
      const summary = summarizeApiError(e, { fallbackTitle: "Couldn't disconnect" });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <GlowCard glow>
      <motion.div variants={fadeUp} initial="hidden" animate="show" className="p-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-500">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold">
                  Google Analytics + Search Console
                </h2>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Connected
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {status.google_user_email ?? 'unknown account'} ·{' '}
                {status.connected_at
                  ? new Date(status.connected_at).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })
                  : ''}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onDisconnect}
            disabled={disconnect.isPending}
          >
            {disconnect.isPending ? <Spinner className="mr-2" /> : <Unplug className="h-3.5 w-3.5" />}
            Disconnect
          </Button>
        </header>

        {needsPicking ? (
          <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-500">
            <Sparkles className="mr-1 inline h-3.5 w-3.5" />
            Pick a GA4 property and a Search Console site below — until you do,
            your monthly reports will skip the traffic sections.
          </div>
        ) : null}

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-xs">
              <BarChart3 className="h-3.5 w-3.5" />
              GA4 Property
            </Label>
            {props.isLoading ? (
              <div className="flex h-10 items-center rounded-md border border-border/60 bg-card/60 px-3 text-sm text-muted-foreground">
                <Spinner className="mr-2" /> Loading properties…
              </div>
            ) : props.data && props.data.ga4_properties.length === 0 ? (
              <div className="rounded-md border border-border/60 bg-card/60 px-3 py-2 text-xs text-muted-foreground">
                No GA4 properties found on this Google account.
              </div>
            ) : (
              <Select value={ga4} onValueChange={setGa4}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a property…" />
                </SelectTrigger>
                <SelectContent>
                  {(props.data?.ga4_properties ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                      {p.account ? (
                        <span className="ml-1 text-xs text-muted-foreground">
                          · {p.account}
                        </span>
                      ) : null}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-xs">
              <Globe2 className="h-3.5 w-3.5" />
              Search Console Site
            </Label>
            {props.isLoading ? (
              <div className="flex h-10 items-center rounded-md border border-border/60 bg-card/60 px-3 text-sm text-muted-foreground">
                <Spinner className="mr-2" /> Loading sites…
              </div>
            ) : props.data && props.data.gsc_sites.length === 0 ? (
              <div className="rounded-md border border-border/60 bg-card/60 px-3 py-2 text-xs text-muted-foreground">
                No verified sites found on this account.
              </div>
            ) : (
              <Select value={gsc} onValueChange={setGsc}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a site…" />
                </SelectTrigger>
                <SelectContent>
                  {(props.data?.gsc_sites ?? []).map((s) => (
                    <SelectItem key={s.site_url} value={s.site_url}>
                      {s.site_url}
                      <span className="ml-1 text-xs text-muted-foreground">
                        · {s.permission_level}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            <a
              href="https://search.google.com/search-console"
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 hover:text-foreground"
            >
              Need to verify a site? Open Search Console <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <Button onClick={onSave} disabled={!dirty || save.isPending}>
            {save.isPending ? <Spinner className="mr-2" /> : null}
            Save selection
          </Button>
        </div>
      </motion.div>
    </GlowCard>
  );
}

function Bullet({
  icon: Icon,
  text,
}: {
  icon: typeof Lock;
  text: React.ReactNode;
}): JSX.Element {
  return (
    <li className="flex items-start gap-2 text-muted-foreground">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <span>{text}</span>
    </li>
  );
}
